// =====================================================================
// tpCustomer — Customer-churn metrics self-tests
//
// The project has no test runner — this file is a hand-rolled assertion
// suite. Two run modes:
//
//   1. In the browser: it auto-runs on load (gated by ?churnTest=1 or
//      localStorage.churnTest='1' so it doesn't slow normal page loads),
//      logging PASS / FAIL summaries to the console. The global
//      window.tpChurnSelfTest() runs it on demand from devtools.
//
//   2. From node, for CI-like usage:
//        node -e "global.window={}; require('./tpCustFe/pages/churn-metrics.js'); require('./tpCustFe/pages/churn-metrics-tests.js'); process.exit(window.tpChurnSelfTest().failed === 0 ? 0 : 1)"
//      The window shim is minimal — churn-metrics.js only touches
//      `window.tpChurn = ...`, which works on any plain object.
//
// All numbers below are pence (so £100k = 10_000_000). Test data is
// synthetic and self-contained: edits to live seed data must not affect
// these expectations.
// =====================================================================

(function () {
  'use strict';
  const tpC = (typeof window !== 'undefined' && window.tpChurn);
  if (!tpC) {
    if (typeof console !== 'undefined') console.error('churn-metrics-tests: tpChurn not available; load churn-metrics.js first');
    return;
  }

  function makeRunner() {
    let passed = 0, failed = 0;
    const failures = [];

    function eqDeep(a, b) {
      if (a === b) return true;
      if (typeof a !== typeof b) return false;
      if (typeof a !== 'object' || !a || !b) return false;
      const ak = Object.keys(a), bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      for (const k of ak) if (!eqDeep(a[k], b[k])) return false;
      return true;
    }

    function approx(a, b, tol) {
      return Math.abs(a - b) <= (tol == null ? 1e-9 : tol);
    }

    function assert(name, cond, detail) {
      if (cond) { passed++; }
      else {
        failed++;
        failures.push(`✗ ${name}` + (detail ? ` — ${detail}` : ''));
      }
    }

    function assertEq(name, actual, expected) {
      const ok = eqDeep(actual, expected);
      assert(name, ok, ok ? null : `expected ${JSON.stringify(expected)}, got ${JSON.stringify(actual)}`);
    }
    function assertApprox(name, actual, expected, tol) {
      const ok = (actual == null && expected == null) || (actual != null && expected != null && approx(actual, expected, tol));
      assert(name, ok, ok ? null : `expected ~${expected}, got ${actual}`);
    }

    function summary() {
      const total = passed + failed;
      const out = `tpChurn self-test: ${passed}/${total} passed${failed ? `, ${failed} FAILED` : ''}`;
      if (typeof console !== 'undefined') {
        if (failed) {
          console.group('%c' + out, 'color:#dc2626;font-weight:600');
          for (const f of failures) console.log(f);
          console.groupEnd();
        } else {
          console.log('%c' + out, 'color:#16a34a;font-weight:600');
        }
      }
      return { passed, failed, total, failures };
    }

    return { assert, assertEq, assertApprox, summary };
  }

  // ── Fixtures ──────────────────────────────────────────────────────
  // A small, deterministic set chosen so the expected outputs are easy
  // to verify by hand.
  //
  // Today is anchored at 2026-05-25 (matching the user's auto-memory)
  // so anyone running these tests at a different date gets the same
  // result. We pass `now` explicitly to every period helper.
  const NOW = new Date('2026-05-25T00:00:00Z');

  // 6 accounts: 1 active healthy, 1 active recently-renewed, 1 active
  // up-for-renewal in 30 days, 1 at-risk in 60 days, 1 churned in past,
  // 1 active with no renewal data yet.
  const accounts = [
    { accountId: 'a1', accountName: 'Alpha',   status: 'active',  arrPence: 12_000_000, contractStart: '2025-03-01', renewalDate: '2026-03-01' },
    { accountId: 'a2', accountName: 'Bravo',   status: 'active',  arrPence: 18_000_000, contractStart: '2024-09-01', renewalDate: '2026-09-01' },
    { accountId: 'a3', accountName: 'Charlie', status: 'active',  arrPence:  6_000_000, contractStart: '2025-06-24', renewalDate: '2026-06-24' },
    { accountId: 'a4', accountName: 'Delta',   status: 'at_risk', arrPence: 24_000_000, contractStart: '2024-07-20', renewalDate: '2026-07-20' },
    { accountId: 'a5', accountName: 'Echo',    status: 'churned', arrPence:  9_500_000, contractStart: '2023-12-01', renewalDate: '2026-01-15' },
    { accountId: 'a6', accountName: 'Foxtrot', status: 'active',  arrPence:  5_000_000, contractStart: '2026-01-10', renewalDate: null },
  ];

  // Renewal events: Alpha just renewed (Mar 2026), Echo churned (Jan
  // 2026), Bravo's last cycle was an upgrade (Sep 2025). Period =
  // rolling 12 months ending today, so all three fall in window.
  const renewalEvents = [
    // Alpha — flat renewal in window
    { id: 're1', accountId: 'a1', renewalDate: '2026-03-01', outcome: 'renewed',     startArrPence: 12_000_000, endArrPence: 12_000_000, arrChangePence: 0 },
    // Bravo — last upgrade (pre-period)
    { id: 're2', accountId: 'a2', renewalDate: '2025-09-01', outcome: 'upgraded',    startArrPence: 14_000_000, endArrPence: 18_000_000, arrChangePence: 4_000_000 },
    // Echo — churned in window
    { id: 're3', accountId: 'a5', renewalDate: '2026-01-15', outcome: 'churned',     startArrPence:  9_500_000, endArrPence: 0,          arrChangePence: -9_500_000 },
    // A historic downgrade in window (anchored to some other account
    // that no longer carries a qual row — exercises the calc on its own)
    { id: 're4', accountId: 'a7', renewalDate: '2026-04-01', outcome: 'downgraded',  startArrPence:  8_000_000, endArrPence: 6_000_000,  arrChangePence: -2_000_000 },
  ];

  // 3 months of snapshots for Charlie: prior-month logins 30, current 10
  // → drop ratio 0.33 < 0.5 (login-drop rule trips). seats 2/30=0.067
  // < 0.4 (seats rule trips). health 35 (<50 trips). dsll 45 (>21 trips).
  // All four rules trip — auto-flag suggests at-risk.
  const snapshotsCharlie = [
    { id: 's1', accountId: 'a3', snapshotMonth: '2026-03-01', logins30d: 30, daysSinceLastLogin: 7,  activeSeats: 14, licensedSeats: 30, healthScore: 80 },
    { id: 's2', accountId: 'a3', snapshotMonth: '2026-04-01', logins30d: 25, daysSinceLastLogin: 12, activeSeats: 10, licensedSeats: 30, healthScore: 65 },
    { id: 's3', accountId: 'a3', snapshotMonth: '2026-05-01', logins30d: 10, daysSinceLastLogin: 45, activeSeats: 2,  licensedSeats: 30, healthScore: 35 },
  ];

  // Alpha snapshots: healthy stable. No rules trip.
  const snapshotsAlpha = [
    { id: 'sa1', accountId: 'a1', snapshotMonth: '2026-03-01', logins30d: 22, daysSinceLastLogin: 6, activeSeats: 18, licensedSeats: 25, healthScore: 86 },
    { id: 'sa2', accountId: 'a1', snapshotMonth: '2026-04-01', logins30d: 24, daysSinceLastLogin: 5, activeSeats: 19, licensedSeats: 25, healthScore: 88 },
    { id: 'sa3', accountId: 'a1', snapshotMonth: '2026-05-01', logins30d: 21, daysSinceLastLogin: 6, activeSeats: 18, licensedSeats: 25, healthScore: 85 },
  ];

  // ── Run the suite ────────────────────────────────────────────────
  function run() {
    const r = makeRunner();

    // — period helper —
    const period = tpC.presetPeriod('rolling-12', NOW);
    r.assertEq('presetPeriod key', period.key, 'rolling-12');
    r.assert('period to is 2026-05-25', period.to.toISOString().slice(0, 10) === '2026-05-25');
    r.assert('period from is 2025-05-25', period.from.toISOString().slice(0, 10) === '2025-05-25');

    // — current-state tiles —
    const active = tpC.activeLogos(accounts);
    r.assertEq('activeLogos.value (4 active)', active.value, 4);
    r.assertEq('activeLogos.n', active.n, 4);

    const live = tpC.liveArr(accounts);
    // 12m + 18m + 6m + 5m = 41m pence
    r.assertEq('liveArr.value (sum of active ARR)', live.value, 41_000_000);

    // — renewal pool (next 90 days from NOW=2026-05-25) —
    // a3 (24 Jun, 30d), a4 (20 Jul, 56d) qualify. a1 (1 Mar, past) excluded.
    // a2 (1 Sep) excluded (~99 days out). a5 excluded (churned). a6 (no date) excluded.
    const pool = tpC.arrUpForRenewal(accounts, NOW, 90);
    r.assertEq('arrUpForRenewal n', pool.n, 2);
    r.assertEq('arrUpForRenewal value', pool.value, 6_000_000 + 24_000_000);

    // — at-risk ARR —
    const atRisk = tpC.atRiskArr(accounts, NOW, 90);
    r.assertEq('atRiskArr n', atRisk.n, 1);
    r.assertEq('atRiskArr value', atRisk.value, 24_000_000);
    r.assertEq('atRiskArr pool size', atRisk.pool, 2);

    // — period-bound churn counts —
    const churned = tpC.logosChurnedInPeriod(renewalEvents, period);
    r.assertEq('logosChurnedInPeriod (Echo only)', churned.value, 1);

    const arrLost = tpC.arrChurnedInPeriod(renewalEvents, period);
    // Echo (9.5m) + downgrade re4 (2m) = 11.5m
    r.assertEq('arrChurnedInPeriod (churn + downgrade)', arrLost.value, 11_500_000);

    // — GRR / NRR —
    // Cohort = events in window = re1, re3, re4 (re2 is Sep 2025, before
    // 25 May 2025? No — 2025-09-01 is AFTER 2025-05-25, so re2 IS in
    // window. Adjust: cohort = re1, re2, re3, re4.
    // startArr = 12m + 14m + 9.5m + 8m = 43.5m
    // lost (churn + downgrade) = 9.5m + 2m = 11.5m
    // change (signed sum) = 0 + 4m - 9.5m - 2m = -7.5m
    const grr = tpC.grrInPeriod(renewalEvents, period);
    r.assertEq('GRR cohort n', grr.n, 4);
    r.assertEq('GRR startArr', grr.startArr, 43_500_000);
    r.assertApprox('GRR value', grr.value, (43_500_000 - 11_500_000) / 43_500_000, 1e-9);

    const nrr = tpC.nrrInPeriod(renewalEvents, period);
    r.assertApprox('NRR value', nrr.value, (43_500_000 - 7_500_000) / 43_500_000, 1e-9);

    // — Renewal calendar (12 months from today) —
    const calendar = tpC.renewalCalendar(accounts, NOW, 12);
    r.assertEq('calendar length', calendar.length, 12);
    const juneBucket = calendar.find(b => b.month.startsWith('2026-06'));
    r.assert('June bucket has Charlie', juneBucket && juneBucket.accounts.length === 1 && juneBucket.accounts[0].accountId === 'a3');
    const julyBucket = calendar.find(b => b.month.startsWith('2026-07'));
    r.assertEq('July bucket health is red (Delta at-risk)', julyBucket.health, 'red');
    const sepBucket = calendar.find(b => b.month.startsWith('2026-09'));
    r.assertEq('Sep bucket has Bravo', sepBucket.accounts[0].accountId, 'a2');
    const churned_in_calendar = calendar.find(b => b.accounts.find(x => x.accountId === 'a5'));
    r.assert('calendar excludes churned accounts', !churned_in_calendar);

    // — ARR bridge —
    const bridge = tpC.arrBridge(renewalEvents, period);
    r.assertEq('bridge length', bridge.length, 12);
    // Echo churn in Jan 2026 → bucket.churn = -9.5m
    const janBucket = bridge.find(b => b.month.startsWith('2026-01'));
    r.assertEq('Jan bridge churn', janBucket.churn, -9_500_000);
    // Bravo upgrade Sep 2025
    const sepBridge = bridge.find(b => b.month.startsWith('2025-09'));
    r.assertEq('Sep bridge upgrade', sepBridge.upgrade, 4_000_000);
    // Net adds: net = newArr + upgrade + downgrade + churn
    r.assertEq('Jan bridge net', janBucket.net, -9_500_000);

    // — Retention trend by cohort month —
    const trend = tpC.retentionTrend(renewalEvents, period);
    const trendMar = trend.find(t => t.month.startsWith('2026-03'));
    r.assertApprox('Mar trend GRR (Alpha flat)', trendMar.grr, 1, 1e-9);
    r.assertApprox('Mar trend NRR', trendMar.nrr, 1, 1e-9);
    const trendJan = trend.find(t => t.month.startsWith('2026-01'));
    r.assertApprox('Jan trend GRR (Echo churn = 0)', trendJan.grr, 0, 1e-9);

    // — Logo count trend —
    const logoLine = tpC.logoCountTrend(accounts, renewalEvents, period);
    r.assertEq('logo trend length', logoLine.length, 12);
    // By end of May 2026 (latest bucket): a1, a2, a3, a4, a6 active; a5
    // churned in Jan 2026 → not counted. = 5 logos.
    const last = logoLine[logoLine.length - 1];
    r.assertEq('logo count latest month', last.active, 5);
    // Before Echo churns (Dec 2025 bucket): a5 still counted, a6 (starts
    // 10 Jan 2026) not yet active. a1, a2, a3, a4, a5 = 5.
    const dec = logoLine.find(b => b.month.startsWith('2025-12'));
    r.assertEq('logo count Dec 2025 (before Echo churn, before Foxtrot start)', dec.active, 5);

    // — At-risk rule evaluation —
    const charlieEval = tpC.evaluateAtRiskRules(
      snapshotsCharlie[2], snapshotsCharlie[1]
    );
    r.assertEq('Charlie trip count (all four trip)', charlieEval.tripCount, 4);
    r.assertEq('Charlie suggests at-risk', charlieEval.suggested, true);

    const alphaEval = tpC.evaluateAtRiskRules(
      snapshotsAlpha[2], snapshotsAlpha[1]
    );
    r.assertEq('Alpha trip count (zero)', alphaEval.tripCount, 0);
    r.assertEq('Alpha does NOT suggest at-risk', alphaEval.suggested, false);

    // Edge case — only one snapshot (no prior): logins rule can't trip
    // because there's no prior to compare with. Other rules still
    // evaluate normally.
    const onlyOneEval = tpC.evaluateAtRiskRules(snapshotsCharlie[2], null);
    r.assertEq('1-snapshot Charlie: 3 rules trip (logins skipped)', onlyOneEval.tripCount, 3);
    r.assertEq('1-snapshot Charlie still suggests at-risk', onlyOneEval.suggested, true);

    // — latestAndPrior —
    const snaps = [...snapshotsAlpha, ...snapshotsCharlie];
    const lp = tpC.latestAndPrior(snaps, 'a3');
    r.assertEq('latest is May 2026 snapshot', lp.latest.snapshotMonth, '2026-05-01');
    r.assertEq('prior is April 2026 snapshot', lp.prior.snapshotMonth, '2026-04-01');

    // — Formatting —
    r.assertEq('formatPounds 12_345_678 pence', tpC.formatPounds(12_345_678), '£123,457');
    r.assertEq('formatPounds zero', tpC.formatPounds(0), '£0');
    r.assertEq('formatPounds negative', tpC.formatPounds(-1_000_000), '-£10,000');
    r.assertEq('formatPoundsCompact £1.2M', tpC.formatPoundsCompact(120_000_000), '£1.2M');
    r.assertEq('formatPoundsCompact £25k', tpC.formatPoundsCompact(2_500_000), '£25k');
    r.assertEq('formatPct 0.87', tpC.formatPct(0.87), '87%');
    r.assertEq('formatPct null', tpC.formatPct(null), '—');

    // — daysToRenewal / tenureMonths —
    r.assertEq('daysToRenewal Charlie (24 Jun - 25 May)', tpC.daysToRenewal(accounts[2], NOW), 30);
    // Bravo contractStart 2024-09-01 vs NOW 2026-05-25 → 631 days / 30 = 21 months (floor).
    r.assertEq('tenureMonths Bravo (~21 months)', tpC.tenureMonths(accounts[1], NOW), 21);

    return r.summary();
  }

  const api = (typeof window !== 'undefined') ? window : (typeof global !== 'undefined' ? global : {});
  api.tpChurnSelfTest = run;

  // Auto-run in the browser if requested. Keep this off by default so
  // it doesn't add console noise to normal sessions.
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const wantsAutoRun =
      (typeof location !== 'undefined' && /[?&]churnTest=1/.test(location.search))
      || (typeof localStorage !== 'undefined' && localStorage.getItem('churnTest') === '1');
    if (wantsAutoRun) {
      // Defer so any test additions later in the file are picked up.
      setTimeout(run, 0);
    }
  }
}());
